export interface FormFieldConfig {
  label: string;
  placeholder: string;
  infoText: string;
  errorMsg: string;
}

export const UNIVERSITY_FORM_CONFIG: Record<string, FormFieldConfig> = {
  fullNameOfInstitution: {
    label: "Full Name of the Institution",
    placeholder: "Enter official institution name as registered with the UGC",
    infoText: "Enter the full and exact name of your institution as it appears in official records registered with the University Grants Commission (UGC). Do not use short forms or abbreviations.",
    errorMsg: "Institution name is required."
  },
  institutionType: {
    label: "Institution Type",
    placeholder: "Select Institution Type",
    infoText: "Select the category that correctly describes your institution. If your institution falls under more than one category, select the most appropriate one. Only recognised and registered institutions are eligible to apply.",
    errorMsg: "Institution type is required."
  },
  city: {
    label: "City",
    placeholder: "Enter city name",
    infoText: "Provide the name of the city or town where your institution's main campus is located.",
    errorMsg: "City is required."
  },
  state: {
    label: "State",
    placeholder: "Select State",
    infoText: "Provide the name of the state or union territory.",
    errorMsg: "State is required."
  },
  institutionWebsite: {
    label: "Institution Website",
    placeholder: "https://yourinstitution.ac.in",
    infoText: "Enter the complete URL of your institution's official website. Personal or departmental websites will not be accepted. Ensure the link is active and publicly accessible.",
    errorMsg: "Official institutional website URL is required."
  },
  naacAccreditationGrade: { label: "NAAC Accreditation Grade", placeholder: "e.g., A++ (2024) or NA", infoText: "If your institution has received a grade from the National Assessment and Accreditation Council (NAAC), enter the grade along with the year of assessment. If not assessed, along with the year in which the assessment was conducted.If your institution has not been assessed by NAAC, write NA", errorMsg: "Maximum length exceeded (200 characters)." },

  previousEngagementWithIndiaAI: {
    label: "Previous Engagement with IndiaAI Initiatives",
    placeholder: "Describe any past engagements or state 'No previous engagement'",
    infoText: "Indicate whether your institution has previously engaged with any IndiaAI initiative, including FutureSkills fellowships, IADI challenges, AIKosh, or any other IndiaAI programme. Describe nature, year, and outcomes. ",
    errorMsg: "Maximum length exceeded (2000 characters)."
  },
  // Part A: NIRF ranking (optional)
  nirfRanking: {
    label: "NIRF Ranking, if applicable",
    placeholder: "e.g., Rank 45, University category, 2024",
    infoText: "Provide the NIRF rank, the category (Overall, University, Engineering, Management, or other), and the year of ranking.",
    errorMsg: "Maximum length exceeded (500 characters)."
  },
  participatingDepartments: {
    label: "Participating Departments or Programmes",
    placeholder: "e.g., Department of Computer Science, 200 undergraduate students...",
    infoText: "List all departments or academic programmes that will be involved in this engagement. For each department, mention the level of study (undergraduate, postgraduate, or doctoral) and the approximate number of students who will participate.",
    errorMsg: "Participating departments profile description is required."
  },

  relevantSectors: {
    label: "Relevant AIKosh Sectors",
    placeholder: "Select all that apply",
    infoText: "From the sectors available on AIKosh — including agriculture, healthcare, education, governance, environment, language, finance, and others — select which are most relevant.",
    errorMsg: "Please select at least one sector."
  },
  otherSectorDetails: {
    label: "Specify Other Sector",
    placeholder: "Please specify your sector",
    infoText: "Provide details if your matching academic focus field falls under 'Others'.",
    errorMsg: "Please specify your sector."
  },
  aiDataRelevance: {
    label: "AI & Data Applications Connection",
    placeholder: "Provide specific application examples...",
    infoText: "Describe, with specific examples, how artificial intelligence or data-related tools and methods are already being used, or could be usefully applied, in the work of the departments listed above. Generic statements about the importance of AI will not be considered adequate. Applicants are expected to demonstrate actual or potential use cases relevant to their field.",
    errorMsg: "AI & Data relevance examples are required."
  },
  selectAllReasoning: {
    label: "Sector Alignment Reasoning",
    placeholder: "Explain why these sectors align...",
    infoText: "From the thematic sectors available on the AIKosh platform, including agriculture, healthcare, education, governance, environment, language, finance, and others, identify which sectors are most directly relevant to your participating departments. Explain clearly why these sectors align with your institution's academic focus or ongoing work.",
    errorMsg: "Maximum length exceeded (2000 characters)."
  },

  connectionWithExistingCoursework: {
    label: "Integration with Existing Coursework",
    placeholder: "Describe integration into current courses, modules, or projects...",
    infoText: "Describe how participation in AIKosh training can be meaningfully integrated into courses, projects, or research activities that are already part of your academic calendar. Please refer to specific subjects, modules, or assignments where AIKosh datasets or tools could be directly applied. A vague response indicating general interest will not be treated as adequate.",
    errorMsg: "Maximum length exceeded (2000 characters)."
  },

  // Part B: relevance of AIKosh sectors to participating departments
  sectorRelevanceReasoning: {
    label: "Relevance of AIKosh Sectors to Participating Departments",
    placeholder: "Explain which AIKosh sectors are most relevant and why (200 words)...",
    infoText: "From the sectors available on AIKosh, including agriculture, healthcare, education, governance, environment, language, finance, and others, which are most relevant to your participating departments and why?",
    errorMsg: "This field is required and must not exceed 1500 characters (approx. 200 words)."
  },

  fullNameContactPerson: {
    label: "Full Name of Contact Person",
    placeholder: "Enter contact person's official name",
    infoText: "Full name of the contact person submitting this application on behalf of the institution as it appears in official records.",
    errorMsg: "Contact person full name is required."
  },
  designation: {
    label: "Designation",
    placeholder: "e.g., Professor / Dean / Registrar",
    infoText: "Provide your full official designation inside the institution.",
    errorMsg: "Designation details are required."
  },
  department: {
    label: "Department",
    placeholder: "e.g., Department of Information Technology",
    infoText: "Provide the name of the department or administrative office you represent.",
    errorMsg: "Department classification is required."
  },
  officialEmailAddress: {
    label: "Official Email Address",
    placeholder: "username@yourinstitution.ac.in",
    infoText: "Please provide an institutional email address. Personal email addresses (Gmail, Yahoo, etc.) will not be accepted.",
    errorMsg: "A valid institutional email address is required."
  },
  phoneNumber: {
    label: "Phone Number",
    placeholder: "10-digit mobile number",
    infoText: "Provide a working 10-digit mobile or office phone number where you can be reached during working hours.",
    errorMsg: "A valid 10-digit phone number is required."
  },
  suitabilityOfContactPerson: {
    label: "Suitability to Coordinate",
    placeholder: "Describe your coordination capability and tracking history...",
    infoText: "Explain why the person named above is the appropriate individual to coordinate this engagement on behalf of the institution. Describe their current role, their access to students and faculty, and any prior experience coordinating workshops, government programmes, or large-scale institutional events. IndiaAI will use this response to assess the suitability of the contact person for end-to-end coordination.",
    errorMsg: "Maximum length exceeded (2000 characters)."
  },

  // Part C: faculty evaluator for Level B hackathon submissions (optional)
  facultyEvaluatorDetails: {
    label: "Faculty Evaluator for Level B Hackathon Submissions (Optional)",
    placeholder: "Provide name, designation, department, and relevant experience...",
    infoText: "Has a faculty member been identified to evaluate Level B hackathon submissions? The faculty evaluator should have familiarity with AI, data science, or a related technical domain. If identified, please provide their name, designation, department, and a brief description of relevant experience. This must be confirmed before shortlisting if not provided here.",
    errorMsg: "Maximum length exceeded (2000 characters)."
  },

  // Part C: alternate operational coordinator, free-text (optional)
  alternativeCoordinatorDetails: {
    label: "Alternate Operational Coordinator Details (Optional)",
    placeholder: "Provide name, designation, and contact information...",
    infoText: "If the person submitting this application is not best placed to serve as the operational SPOC, please provide details of a more suitable coordinator within the institution, including their name, designation, and contact information. The SPOC will be responsible for all coordination with IndiaAI before, during, and after the workshop, including venue arrangements, student mobilisation, and post-engagement reporting.",
    errorMsg: "Maximum length exceeded (2000 characters)."
  },

  // Part C: preferred hosting window within the programme timeline
  preferredWorkshopTimeline: {
    label: "Preferred Month/Fortnight for Workshop (15 Sep 2026 - 31 Mar 2027)",
    placeholder: "e.g., February 2027 or 'Flexible with the given timeline'",
    infoText: "The AIKosh University Engagement Programme will run from 15 September 2026 to 31 March 2027. Please indicate the month or fortnight within this window that would work best for your institution to host the workshop. Please mention a month and year when the university would like to host the IndiaAI team for the University Engagement Programme. If unclear, please mention 'Flexible with the given timeline'.",
    errorMsg: "Please indicate your preferred month or fortnight."
  },


  alternateSPOCFullName: {
    label: "Operational SPOC Full Name",
    placeholder: "Enter alternate SPOC name",
    infoText: "If you are not best placed to serve as operational coordinator, provide details of a more suitable Single Point of Contact (SPOC) responsible for end-to-end event execution.",
    errorMsg: ""
  },
  alternateSPOCName: {
    label: "Operational SPOC Reference Name",
    placeholder: "Enter nickname/short name",
    infoText: "Short name reference identifier for communication paths.",
    errorMsg: ""
  },
  alternateSPOCEmail: {
    label: "Operational SPOC Email",
    placeholder: "spoc@yourinstitution.ac.in",
    infoText: "Official institutional email address for the operational SPOC tracking interface.",
    errorMsg: "Please enter a valid official email address layout."
  },
  alternateSPOCDesignation: {
    label: "Operational SPOC Designation",
    placeholder: "e.g., Assistant Professor / Placement Officer",
    infoText: "Official position of the nominated operational coordinator.",
    errorMsg: ""
  },
  alternateSPOCPhone: {
    label: "Operational SPOC Phone",
    placeholder: "SPOC contact number",
    infoText: "Working phone connection link to manage active day-to-day facilitator scheduling communications.",
    errorMsg: "Please provide a valid contact number format."
  },
  studentConveningMechanisms: {
    label: "Student Mobilisation & Convening Mechanisms",
    placeholder: "e.g., Innovation Cell (150 members), Data Science Club (80 members)...",
    infoText: "Describe the student groups, clubs, or institutional bodies through which students can be convened and mobilised for this workshop. Mention the name of each group, its approximate active membership, and how it would be used to gather participants. For example: Innovation Cell, Data Science Club, Entrepreneurship Cell, Placement Cell, departmental student associations, etc.",
    errorMsg: "Maximum length exceeded (2000 characters)."
  },

  preferredAvailabilityFrom: {
    label: "Preferred Availability From",
    placeholder: "Select Start Date",
    infoText: "Indicate preferred availability window start month or academic period for hosting the on-campus workshop block.",
    errorMsg: "Preferred selection start date is required."
  },
  preferredAvailabilityTo: {
    label: "Preferred Availability To",
    placeholder: "Select End Date",
    infoText: "Indicate availability window tracking boundary conclusion timeline parameter.",
    errorMsg: "Preferred selection end date is required."
  },
  eventAlignmentNote: {
    label: "Existing Event Alignment Notes",
    placeholder: "e.g., Annual Tech Festival (October 12-14)...",
    infoText: "If the workshop could align with planned events, technical festivals, or seminars, mention them here with indicative dates. ",
    errorMsg: ""
  },
  expectedStudentParticipants: {
    label: "Expected Student Participants Count",
    placeholder: "Select Number",
    infoText: "Approximately how many students do you expect to participate? Provide a realistic estimate reflecting venue capacity and outreach reach.",
    errorMsg: "Expected attendance scope metric selection is required."
  },
  additionalInformation: {
    label: "Additional Context Support",
    placeholder: "Any notable achievements or extra context...",
    infoText: "Include any additional information, notable achievements, or special context that strengthens your institution's application.",
    errorMsg: ""
  }
}